import { attachRouterTracing } from './sentry';

// eslint-disable-next-line no-console -- deliberate release stamp in every console
console.info('[build]', __COMMIT_HASH__);

// Load-bearing against zod's sideEffects:false tree-shaking — mechanism and
// pin: scripts/check-zod-locale.mts (runs after every build).
z.config(z.locales.en());
import '@rainbow-me/rainbowkit/styles.css';
import './tailwind.css';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  RainbowKitProvider,
  darkTheme,
  lightTheme,
} from '@rainbow-me/rainbowkit';
import { StrictMode, type ReactNode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { z } from 'zod';
import { track } from '@/shared/utils/track';
import '@/shared/utils/posthog';
import { wagmiConfig } from '@/shared/utils/wagmiConfig';
import { router } from './routes';

attachRouterTracing(router);
import ColorThemeProvider, { useColorTheme } from './ColorThemeProvider';
import UiError from './UiError';

function ThemedRainbowKit({ children }: { children: ReactNode }) {
  const { isDark } = useColorTheme();
  return (
    <RainbowKitProvider theme={isDark ? darkTheme() : lightTheme()}>
      {children}
    </RainbowKitProvider>
  );
}

// Mutations never enter react-query's offline pause (networkMode 'always'):
// while BackendStatusProvider holds onlineManager offline during a gateway
// outage, a paused mutation would queue silently and auto-fire on recovery
// — a money action executing later without the user re-initiating it.
// Writes must fail fast and honestly instead; only queries pause.
const queryClient = new QueryClient({
  defaultOptions: { mutations: { networkMode: 'always' } },
});

async function startApp() {
  if (import.meta.env.VITE_MOCK === 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  if (window.location.pathname === '/twitter-auth-success') {
    document.body.textContent =
      'You have successfully connected to your Twitter account. You can safely close this tab and return to the application.';
  } else if (window.location.pathname === '/twitter-auth-failure') {
    document.body.textContent =
      'Something went wrong. Close this tab and try again.';
  } else {
    const rootEl = document.getElementById('root');
    if (rootEl === null) throw new Error('#root missing from index.html');
    createRoot(rootEl).render(
      <StrictMode>
        <Sentry.ErrorBoundary
          fallback={<UiError />}
          onError={() => track('error_page_shown')}
        >
          <WagmiProvider config={wagmiConfig}>
            <QueryClientProvider client={queryClient}>
              <ColorThemeProvider>
                <ThemedRainbowKit>
                  <RouterProvider router={router} />
                </ThemedRainbowKit>
              </ColorThemeProvider>
            </QueryClientProvider>
          </WagmiProvider>
        </Sentry.ErrorBoundary>
      </StrictMode>,
    );
  }
}

void startApp();
